/**
 * Serializer: Converts React Flow format → DiagramData JSON
 */

import { Node, Edge } from '@xyflow/react'
import type {
  DiagramData,
  DiagramNode,
  DiagramEdge,
  Container,
  DiagramType,
  DiagramLayer,
  DiagramComment,
  NodeType,
} from '../types/schema'
import { STRUCTURED_CONTENT_KEYS } from '../types/schema'
import { autoCleanAndValidate } from './jsonCleaner'

export interface SerializeOptions {
  includeViewport?: boolean
  includeMetadata?: boolean
  prettify?: boolean
  /** Diagram layers to preserve on the serialized document (draw.io-style). */
  layers?: DiagramLayer[]
  /** Comment / annotation threads to preserve on the serialized document. */
  comments?: DiagramComment[]
  /** Editor snap-to-grid toggle to persist on the document. */
  snapToGrid?: boolean
  /** Editor grid spacing (flow units) to persist on the document. */
  gridSize?: number
}

/**
 * Serialize React Flow nodes/edges back to DiagramData
 */
export function serializeToDiagramData(
  nodes: Node[],
  edges: Edge[],
  diagramType: DiagramType,
  options: SerializeOptions = {}
): DiagramData {
  const containers: Container[] = []
  const diagramNodes: DiagramNode[] = []

  // Create a map of node ID to node for parent lookup
  const nodeMap = new Map<string, Node>()
  nodes.forEach(node => nodeMap.set(node.id, node))

  // Separate containers from regular nodes
  nodes.forEach((node) => {
    // Handle both old ('container') and new ('group-container') type names
    if (node.type === 'container' || node.type === 'group-container') {
      containers.push(serializeContainer(node))
    } else {
      diagramNodes.push(serializeNode(node, nodeMap))
    }
  })

  const diagramData: DiagramData = {
    diagramType,
    nodes: diagramNodes,
    edges: edges.map(serializeEdge),
    containers: containers.length > 0 ? containers : undefined,
    layers: options.layers && options.layers.length > 0 ? options.layers : undefined,
  }

  // Comments / annotation threads. The key is added ONLY when comments exist so
  // documents that never used comments stay byte-identical (back-compat).
  if (options.comments && options.comments.length > 0) {
    diagramData.comments = options.comments
  }

  // Editor grid (snap-to-grid). Only emitted when explicitly provided so
  // documents that never used the grid stay byte-identical (back-compat).
  if (options.snapToGrid !== undefined) diagramData.snapToGrid = options.snapToGrid
  if (options.gridSize !== undefined) diagramData.gridSize = options.gridSize

  return diagramData
}

/**
 * Convert React Flow container node back to Container
 */
function serializeContainer(node: Node): Container {
  const data = node.data as any
  return {
    id: node.id,
    label: data?.label as string,
    type: data?.containerType as Container['type'],
    position: node.position,
    size: {
      width: node.style?.width as number || node.width || 400,
      height: node.style?.height as number || node.height || 300,
    },
    style: {
      bgColor: data?.bgColor || node.style?.backgroundColor as string,
      borderColor: data?.borderColor || node.style?.borderColor as string,
      borderStyle: data?.borderStyle || node.style?.borderStyle as any,
      opacity: data?.fillOpacity ?? node.style?.opacity as number,
      headerBgColor: data?.headerBgColor as string,
      headerTextColor: data?.headerTextColor as string,
      textColor: data?.textColor as string,
    },
    icon: data?.icon as string,
    description: data?.description as string,
    metadata: data?.metadata as Record<string, any>,
    // Layer assignment (draw.io-style layers).
    layerId: data?.layerId as string | undefined,
  }
}

/**
 * Convert React Flow node back to DiagramNode
 * Converts relative positions back to absolute positions
 */
function serializeNode(node: Node, nodeMap: Map<string, Node>): DiagramNode {
  const data = node.data as any

  // Collect style properties from node.data (set by properties panel)
  const style: any = {
    ...data?.style,
  }

  // Include properties that might be set directly on data
  if (data?.bgColor) style.bgColor = data.bgColor
  if (data?.textColor) style.textColor = data.textColor
  if (data?.borderColor) style.borderColor = data.borderColor
  if (data?.borderWidth !== undefined) style.borderWidth = data.borderWidth
  if (data?.borderStyle) style.borderStyle = data.borderStyle
  if (data?.fontSize !== undefined) style.fontSize = data.fontSize
  if (data?.fontWeight) style.fontWeight = data.fontWeight
  if (data?.fontFamily) style.fontFamily = data.fontFamily
  if (data?.textAlign) style.textAlign = data.textAlign
  if (data?.fillOpacity !== undefined) style.fillOpacity = data.fillOpacity
  // Preserve header color properties for structured nodes and containers
  if (data?.headerBgColor) style.headerBgColor = data.headerBgColor
  if (data?.headerTextColor) style.headerTextColor = data.headerTextColor
  // Preserve text overflow properties
  if (data?.wordWrap !== undefined) style.wordWrap = data.wordWrap
  if (data?.textOverflow) style.textOverflow = data.textOverflow
  if (data?.textPadding !== undefined) style.textPadding = data.textPadding
  if (data?.letterSpacing !== undefined) style.letterSpacing = data.letterSpacing
  if (data?.lineHeight !== undefined) style.lineHeight = data.lineHeight
  if (data?.textDecoration) style.textDecoration = data.textDecoration
  if (data?.textOpacity !== undefined) style.textOpacity = data.textOpacity
  if (data?.verticalAlign) style.verticalAlign = data.verticalAlign

  // Convert relative position to absolute if node has a parent
  let absolutePosition = node.position
  if (node.parentId) {
    const parent = nodeMap.get(node.parentId)
    if (parent) {
      // Position is relative to parent, convert to absolute
      absolutePosition = {
        x: node.position.x + parent.position.x,
        y: node.position.y + parent.position.y,
      }
    }
  }

  return {
    id: node.id,
    label: data?.label as string,
    nodeType: data?.nodeType as NodeType,
    subtype: data?.subtype as string | undefined,
    position: absolutePosition,
    size: {
      width: node.style?.width as number || node.width || 150,
      height: node.style?.height as number || node.height || 80,
    },
    parentContainer: node.parentId || data?.parentContainer as string | undefined,
    layer: data?.layer as DiagramNode['layer'],
    // Layer assignment (draw.io-style layers) — round-trips via node.data.layerId.
    layerId: data?.layerId as string | undefined,
    // Rotation (degrees) — round-trips via node.data.rotation.
    rotation: typeof data?.rotation === 'number' ? data.rotation : undefined,
    // Stacking order (front/back) — round-trips via React Flow's node.zIndex.
    zIndex: typeof node.zIndex === 'number' ? node.zIndex : undefined,
    icon: data?.icon as string | undefined,
    shapeId: data?.shapeId as string | undefined,
    shapeCategory: data?.shapeCategory as string | undefined,
    isShape: data?.isShape as boolean | undefined,
    style: Object.keys(style).length > 0 ? style : undefined,
    description: data?.description as string | undefined,
    metadata: data?.metadata as Record<string, any> | undefined,
    customData: data?.customData as Record<string, any> | undefined,
    // Preserve ALL structured content (table/resource/endpoint/class/state/c4/
    // sequence*/gantt*) from the SINGLE SOURCE OF TRUTH. Iterating the shared
    // list (instead of hardcoding each field) makes "serializer drops a content
    // type" (the "Gantt gone" bug class) structurally impossible — a new content
    // type added to STRUCTURED_CONTENT_KEYS round-trips automatically.
    ...pickStructuredContent(data),
  }
}

/** Copy every structured-content field present on a node's data, from the SSOT list. */
function pickStructuredContent(data: any): Record<string, any> {
  const out: Record<string, any> = {}
  for (const key of STRUCTURED_CONTENT_KEYS) {
    if (data?.[key] !== undefined) out[key] = data[key]
  }
  return out
}

/**
 * Convert React Flow edge back to DiagramEdge
 */
function serializeEdge(edge: Edge): DiagramEdge {
  const data = edge.data as any

  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle ?? undefined,
    targetHandle: edge.targetHandle ?? undefined,
    // Layer assignment (draw.io-style layers) — round-trips via edge.data.layerId.
    layerId: data?.layerId as string | undefined,
    label: edge.label as string | undefined,
    edgeType: data?.edgeType as DiagramEdge['edgeType'],
    type: edge.type as DiagramEdge['type'],
    protocol: data?.protocol as string | undefined,
    method: data?.method as string | undefined,
    description: data?.description as string | undefined,
    animated: edge.animated,
    style: {
      stroke: edge.style?.stroke as string || data?.color,
      strokeWidth: edge.style?.strokeWidth as number || data?.lineWidth,
      strokeDasharray: edge.style?.strokeDasharray as string,
    },
    // Preserve all edge data including offset, styling, labels, etc.
    data: {
      offset: data?.offset,
      edgeDirection: data?.edgeDirection,
      bidirectional: data?.bidirectional,
      lineStyle: data?.lineStyle,
      lineWidth: data?.lineWidth,
      lineCap: data?.lineCap,
      lineJoin: data?.lineJoin,
      color: data?.color,
      opacity: data?.opacity,
      arrowStart: data?.arrowStart,
      arrowEnd: data?.arrowEnd,
      arrowSize: data?.arrowSize,
      labelFontSize: data?.labelFontSize,
      labelBackground: data?.labelBackground,
      labelBackgroundColor: data?.labelBackgroundColor,
      labelZIndex: data?.labelZIndex,
      labelPosition: data?.labelPosition,
      labelMode: data?.labelMode,
      labelText: data?.labelText,
      shadowEnabled: data?.shadowEnabled,
      shadowColor: data?.shadowColor,
      shadowBlur: data?.shadowBlur,
      shadowOffsetX: data?.shadowOffsetX,
      shadowOffsetY: data?.shadowOffsetY,
      zIndex: data?.zIndex,
      // Connector routing shape + fixed-vs-floating attachment (draw.io parity).
      pathStyle: data?.pathStyle,
      targetAngle: data?.targetAngle,
      handlePreference: data?.handlePreference,
      fixedConnection: data?.fixedConnection,
      layerId: data?.layerId,
    },
    metadata: data?.metadata as Record<string, any> | undefined,
  }
}

/**
 * Export to JSON string
 */
export function exportToJSON(
  diagramData: DiagramData,
  prettify: boolean = true
): string {
  return JSON.stringify(diagramData, null, prettify ? 2 : 0)
}

/**
 * Import from JSON string
 * Automatically handles escaped JSON strings
 */
export function importFromJSON(jsonString: string): DiagramData {
  try {
    // Try direct parse first (fast path)
    return JSON.parse(jsonString)
  } catch (error) {
    // If direct parse fails, try cleaning escaped JSON
    const result = autoCleanAndValidate(jsonString)

    if (result.success && result.parsed) {
      return result.parsed
    } else {
      throw new Error(result.error || `Failed to parse JSON: ${error}`)
    }
  }
}

// ============================================================================
// EDIT TRACKING FOR AI-FRIENDLY EXPORT
// ============================================================================

export interface UserEdit {
  nodeId: string
  type: 'moved' | 'styled' | 'added' | 'modified'
  before?: any
  after?: any
}

export interface EditTracker {
  movedNodes: Set<string>
  styledNodes: Set<string>
  addedNodes: Set<string>
  modifiedNodes: Set<string>
  deletedNodes: Set<string>
}

/**
 * Create a new edit tracker
 */
export function createEditTracker(): EditTracker {
  return {
    movedNodes: new Set(),
    styledNodes: new Set(),
    addedNodes: new Set(),
    modifiedNodes: new Set(),
    deletedNodes: new Set(),
  }
}

/**
 * Track node position change
 */
export function trackNodeMove(tracker: EditTracker, nodeId: string) {
  tracker.movedNodes.add(nodeId)
}

/**
 * Track node style change
 */
export function trackNodeStyle(tracker: EditTracker, nodeId: string) {
  tracker.styledNodes.add(nodeId)
}

/**
 * Track node addition
 */
export function trackNodeAdd(tracker: EditTracker, nodeId: string) {
  tracker.addedNodes.add(nodeId)
}

/**
 * Track node modification
 */
export function trackNodeModify(tracker: EditTracker, nodeId: string) {
  tracker.modifiedNodes.add(nodeId)
}

/**
 * Track node deletion
 */
export function trackNodeDelete(tracker: EditTracker, nodeId: string) {
  tracker.deletedNodes.add(nodeId)
}

// ============================================================================
// EXPORT FOR AI (Minimal JSON + Edit Tracking)
// ============================================================================

export interface ExportForAIOptions {
  includeEditMetadata?: boolean
  prettify?: boolean
}

/**
 * Export for AI with minimal JSON + edit tracking
 * Preserves templates/patterns, only expands edited nodes
 */
export function exportForAI(
  originalData: DiagramData,
  currentNodes: Node[],
  _currentEdges: Edge[],
  editTracker: EditTracker,
  options: ExportForAIOptions = {}
): string {
  const { includeEditMetadata = true, prettify = true } = options

  // Start with original data structure (templates, patterns, etc.)
  const exportData: any = {
    schemaVersion: (originalData as any).schemaVersion || 'v2',
    diagramType: originalData.diagramType,
  }

  // Preserve V2 features (templates, patterns)
  if ((originalData as any).nodeTemplates) {
    exportData.nodeTemplates = (originalData as any).nodeTemplates
  }
  if ((originalData as any).nodeReplicas) {
    exportData.nodeReplicas = (originalData as any).nodeReplicas
  }
  if ((originalData as any).nodesFromTemplate) {
    exportData.nodesFromTemplate = (originalData as any).nodesFromTemplate
  }
  if ((originalData as any).edgePatterns) {
    exportData.edgePatterns = (originalData as any).edgePatterns
  }

  // Add containers
  const containers = currentNodes
    .filter(n => n.type === 'group-container' || n.type === 'container')
    .map(serializeContainer)

  if (containers.length > 0) {
    exportData.containers = containers
  }

  // Export nodes: Only expand nodes that were edited
  const editedNodeIds = new Set([
    ...editTracker.movedNodes,
    ...editTracker.styledNodes,
    ...editTracker.addedNodes,
    ...editTracker.modifiedNodes,
  ])

  const nodeMap = new Map<string, Node>()
  currentNodes.forEach(node => nodeMap.set(node.id, node))

  if (editedNodeIds.size > 0) {
    const editedNodes = currentNodes
      .filter(n => editedNodeIds.has(n.id))
      .filter(n => n.type !== 'group-container' && n.type !== 'container')
      .map(node => serializeNode(node, nodeMap))

    exportData.nodes = editedNodes
  } else {
    // No edits - include original nodes if they exist
    if (originalData.nodes) {
      exportData.nodes = originalData.nodes
    }
  }

  // Export edges
  if (originalData.edges) {
    exportData.edges = originalData.edges
  }

  // Add edit metadata for AI context
  if (includeEditMetadata && editedNodeIds.size > 0) {
    exportData._userEdits = {
      moved: Array.from(editTracker.movedNodes),
      styled: Array.from(editTracker.styledNodes),
      added: Array.from(editTracker.addedNodes),
      modified: Array.from(editTracker.modifiedNodes),
      deleted: Array.from(editTracker.deletedNodes),
      summary: `User edited ${editedNodeIds.size} nodes, deleted ${editTracker.deletedNodes.size} nodes`,
    }
  }

  return JSON.stringify(exportData, null, prettify ? 2 : 0)
}

// ============================================================================
// EXPORT FULL (Complete Expanded JSON)
// ============================================================================

export interface ExportFullOptions {
  includeOriginal?: boolean
  prettify?: boolean
}

/**
 * Export full expanded JSON (all nodes, edges explicit)
 * No templates or patterns - everything expanded
 */
export function exportFull(
  originalData: DiagramData,
  currentNodes: Node[],
  currentEdges: Edge[],
  options: ExportFullOptions = {}
): string {
  const { includeOriginal = false, prettify = true } = options

  // Serialize everything to standard format
  const diagramData = serializeToDiagramData(
    currentNodes,
    currentEdges,
    originalData.diagramType
  )

  // Optionally include original template/pattern data for reference
  if (includeOriginal) {
    const exportData: any = { ...diagramData }

    exportData._original = {
      nodeTemplates: (originalData as any).nodeTemplates,
      nodeReplicas: (originalData as any).nodeReplicas,
      nodesFromTemplate: (originalData as any).nodesFromTemplate,
      edgePatterns: (originalData as any).edgePatterns,
    }

    return JSON.stringify(exportData, null, prettify ? 2 : 0)
  }

  return JSON.stringify(diagramData, null, prettify ? 2 : 0)
}
